import { useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ArrowRightIcon from "@atlaskit/icon/core/arrow-right";
import { token } from "@atlaskit/tokens";

import AccountCreation, { Social } from "@/components/signup/account-creation";
import { fontSize } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import { useJiraAiFFL } from "@/templates/paid-landing/src/hooks/use-jira-ai-ffl-context";

import { InputSubmitButton } from "./input-submit-button";

const containerStyles = css({
    display: "flex",
    justifyContent: "space-between",
    margin: "0 auto",
    width: "100%",
    maxWidth: "60.5rem",
    marginBlockEnd: "3rem"
});

const messageStyles = css({
    width: "35.063rem"
});

const messageHeaderStyles = css({
    fontFamily: token("font.family.brand.heading"), // Charlie Display
    fontWeight: 700,
    fontSize: "2rem",
    lineHeight: "2.375rem",
    letterSpacing: "0.019rem",
    color: wacToken("color.neutral.light1000")
});

const messageBodyStyles = css({
    fontFamily: token("font.family.brand.body"), // Charlie Text
    fontSize: "1.25rem",
    lineHeight: "1.75rem",
    color: wacToken("color.neutral.light1000")
});

const messageCtaStyles = css({
    fontFamily: token("font.family.brand.body"), // Charlie Text
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: "1.25rem",
    color: wacToken("color.extended.blue700"),
    cursor: "pointer"
});

const accountCreationStyles = css({
    width: "23rem"
});

const separatorBorder = css({
    borderColor: "inherit",
    borderStyle: "solid",
    borderWidth: 0.125,
    display: "block",
    flexGrow: 1,
    height: 0,
    margin: "0 1rem",
    opacity: 0.25
});

const separatorCss = css({
    fontSize: fontSize.sm,
    display: "flex",
    lineHeight: ".25px",
    margin: "2rem 0",
    textAlign: "center"
});

export const Header = ({ onRetryClick }: { onRetryClick?: () => void }) => {
    const { firePaidJiraAi, promptAttemptIndex } = useJiraAiFFL();
    const messages = {
        yourJiraSpaceIsReady: "Your Jira space is ready",
        getStartedNow:
            "Get started now to keep editing, save your progress, and make it your own.",
        tryAnotherPrompt: "Try another prompt"
    };

    useEffect(() => {
        firePaidJiraAi("AccountCreationViewed", "viewed", {
            promptAttemptIndex
        });
    }, [firePaidJiraAi, promptAttemptIndex]);

    return (
        <div css={containerStyles}>
            <div css={messageStyles}>
                <h2 css={messageHeaderStyles}>
                    {messages.yourJiraSpaceIsReady}
                </h2>
                <p css={messageBodyStyles}>{messages.getStartedNow}</p>
                <p css={messageCtaStyles}>
                    <span
                        onClick={() => onRetryClick?.()}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onRetryClick?.();
                            }
                        }}
                        aria-label={messages.tryAnotherPrompt}
                    >
                        {messages.tryAnotherPrompt}{" "}
                        <ArrowRightIcon
                            label={messages.tryAnotherPrompt}
                            size="small"
                        />
                    </span>
                </p>
            </div>
            <div css={accountCreationStyles}>
                <AccountCreation
                    templateId="cfind-4666-jira-ai-preview"
                    Input={<InputSubmitButton />}
                    Submit={null}
                    Login={null}
                    Separator={<Separator />}
                    Social={<Social providers={{ google: "Google" }} />}
                />
            </div>
        </div>
    );
};

const Separator = () => {
    const intl = useIntl();

    const separatorText = intl.formatMessage({
        id: "WacSignupForm.socialSignUpOrSignUpWith",
        defaultMessage: "Or sign up with"
    });

    return (
        <div css={separatorCss}>
            <div css={separatorBorder} />
            {separatorText}
            <div css={separatorBorder} />
        </div>
    );
};
